import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ZapIcon, UsersIcon, SparklesIcon } from 'lucide-react';
const comparisonData = [{
  old: 'Busca más contactos',
  new: 'Configura quién te encuentra a ti'
}, {
  old: 'Llama a tu lista de 100',
  new: 'Que tu perfil llame por ti'
}, {
  old: 'Persigue a los interesados',
  new: 'Filtra a los calificados antes de hablar'
}, {
  old: 'Depende de tu energía',
  new: 'Depende de tu sistema'
}];
const timelineSteps = [{
  days: 'Día 1',
  task: 'Defines exactamente a quién atraes'
}, {
  days: 'Día 2-3',
  task: 'Configuras cómo te perciben cuando llegan a tu perfil'
}, {
  days: 'Día 4-5',
  task: 'Activas el contenido que genera conversaciones'
}, {
  days: 'Día 6-7',
  task: 'Instalas el cierre sin presión ni persecución'
}];
export function WhyItWorks() {
  return <section className="bg-brand-dark px-6 py-20 md:py-28" data-id="element-292">
      <div className="mx-auto max-w-3xl" data-id="element-293">
        {/* Title */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12" data-id="element-294">
          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight" data-id="element-295">
            Por Qué Este Sistema Funciona Cuando Todo lo Demás{' '}
            <span className="text-brand-gold" data-id="element-296">Falló</span>
          </h2>
          <div className="w-20 h-1 bg-brand-gold mx-auto mt-6 rounded-full" data-id="element-297" />
        </motion.div>

        {/* Intro */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-lg text-slate-300 leading-relaxed text-center mb-16" data-id="element-298">
          No inventamos nada nuevo. Tomamos los principios del marketing de
          atracción que usan las marcas más exitosas del mundo y los tradujimos
          en un plan de 7 días específico para el Networker que tiene poco
          tiempo, pocos seguidores y mucho cansancio de los métodos viejos.
        </motion.p>

        {/* RAZÓN 1 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-299">
          <div className="flex items-center gap-3 mb-6" data-id="element-300">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-gold/10 border border-brand-gold/30 shrink-0" data-id="element-301">
              <CheckCircleIcon className="w-5 h-5 text-brand-gold" data-id="element-302" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-303">
              Razón 1: Ataca el Problema Correcto
            </h3>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto -mx-2" data-id="element-304">
            <table className="w-full min-w-[480px] text-left" data-id="element-305">
              <thead data-id="element-306">
                <tr className="border-b border-slate-700" data-id="element-307">
                  <th className="py-3 px-4 text-sm font-semibold text-red-400 uppercase tracking-wider" data-id="element-308">
                    Lo que te enseñaron
                  </th>
                  <th className="py-3 px-4 text-sm font-semibold text-emerald-400 uppercase tracking-wider" data-id="element-309">
                    Lo que realmente funciona
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-310">
                {comparisonData.map((row, i) => <motion.tr key={i} initial={{
                opacity: 0,
                x: -10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="border-b border-slate-800/60" data-id="element-311">
                    <td className="py-3 px-4 text-slate-400 line-through decoration-red-500/40" data-id="element-312">
                      {row.old}
                    </td>
                    <td className="py-3 px-4 text-white font-medium" data-id="element-313">
                      {row.new}
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-slate-300 leading-relaxed" data-id="element-314">
            Cuando tu perfil está configurado correctamente, el algoritmo de
            Instagram trabaja para ti las 24 horas.{' '}
            <strong className="text-brand-gold" data-id="element-315">
              Tú no trabajas para el algoritmo.
            </strong>
          </p>
        </motion.div>

        {/* RAZÓN 2 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-316">
          <div className="flex items-center gap-3 mb-6" data-id="element-317">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-gold/10 border border-brand-gold/30 shrink-0" data-id="element-318">
              <ZapIcon className="w-5 h-5 text-brand-gold" data-id="element-319" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-320">
              Razón 2: Una Sola Tarea por Día. Sin Parálisis.
            </h3>
          </div>

          <p className="text-slate-300 leading-relaxed mb-6" data-id="element-321">
            La mayoría de los Networkers no fallan por falta de motivación.
            Fallan porque nadie les dio una ruta de pasos concretos que puedan
            ejecutar en{' '}
            <strong className="text-white" data-id="element-322">45 minutos diarios.</strong>
          </p>

          {/* Timeline */}
          <div className="space-y-0 relative ml-4" data-id="element-323">
            <div className="absolute left-[7px] top-3 bottom-3 w-px bg-brand-gold/30" data-id="element-324" />
            {timelineSteps.map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.12
          }} className="flex items-start gap-4 py-3 relative" data-id="element-325">
                <div className="w-[15px] h-[15px] rounded-full bg-brand-gold/20 border-2 border-brand-gold shrink-0 mt-0.5 z-10" data-id="element-326" />
                <div data-id="element-327">
                  <span className="text-brand-gold font-bold text-sm" data-id="element-328">
                    {step.days}
                  </span>
                  <span className="text-slate-300 ml-2" data-id="element-329">→ {step.task}</span>
                </div>
              </motion.div>)}
          </div>

          <p className="mt-6 text-slate-400 italic" data-id="element-330">
            Al final del Día 7 tu perfil no es el mismo de antes. Es una{' '}
            <strong className="text-white not-italic" data-id="element-331">
              vitrina profesional que trabaja sola.
            </strong>
          </p>
        </motion.div>

        {/* RAZÓN 3 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-16" data-id="element-332">
          <div className="flex items-center gap-3 mb-6" data-id="element-333">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-gold/10 border border-brand-gold/30 shrink-0" data-id="element-334">
              <UsersIcon className="w-5 h-5 text-brand-gold" data-id="element-335" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-336">
              Razón 3: Funciona con Menos de 500 Seguidores
            </h3>
          </div>

          <p className="text-slate-300 leading-relaxed" data-id="element-337">
            El algoritmo de Instagram en 2026 distribuye contenido por{' '}
            <strong className="text-white" data-id="element-338">relevancia</strong>, no por tamaño de
            audiencia. Un perfil de 300 seguidores con una bio optimizada y un
            Reel con el gancho correcto puede llegar a miles de personas que
            nunca te conocieron. Eso es exactamente lo que el{' '}
            <strong className="text-brand-gold" data-id="element-339">Día 2</strong> y el{' '}
            <strong className="text-brand-gold" data-id="element-340">Día 4</strong> instalan en tu
            cuenta.
          </p>
        </motion.div>

        {/* RAZÓN 4 */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-4" data-id="element-341">
          <div className="flex items-center gap-3 mb-6" data-id="element-342">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-gold/10 border border-brand-gold/30 shrink-0" data-id="element-343">
              <SparklesIcon className="w-5 h-5 text-brand-gold" data-id="element-344" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-345">
              Razón 4: Cambia tu Identidad Digital, No Solo tu Estrategia
            </h3>
          </div>

          <p className="text-slate-300 leading-relaxed" data-id="element-346">
            La diferencia entre un Networker que persigue y uno que atrae no es
            la técnica que usa. Es{' '}
            <strong className="text-white" data-id="element-347">
              cómo aparece en redes sociales.
            </strong>{' '}
            El Sistema Magnético no te da trucos de venta. Te da la{' '}
            <strong className="text-brand-gold" data-id="element-348">
              identidad digital de un líder
            </strong>{' '}
            que hace que los prospectos correctos quieran acercarse solos.
          </p>
        </motion.div>
      </div>
    </section>;
}